import axios, { AxiosHeaders } from "axios";
import Session from "users/session/session";
import PaginatedResponse from "common/contracts/paginated-response";
import Permission from "permissions/entities/permission/permission";
import AttorneyHubAPIService from "common/services/api/attorney-hub-api-service";
import PermissionAPIResponse from "permissions/entities/permission/api/response-contracts/permission-api-response";
import PermissionQuery from "permissions/entities/permission/api/request-contracts/permission-query";
import PermissionCommand from "./request-contracts/permission-command";
import Guid from "common/values/guid/guid";
import BulkPermissionCommand from "permissions/entities/permission/api/request-contracts/bulk-permission-command";

export default class PermissionsAPIService {
  private readonly headers: AxiosHeaders = new AxiosHeaders();

  private headersWithJson(): AxiosHeaders {
    return this.headers.concat({ "Content-Type": "application/json" });
  }

  constructor(session: Readonly<Session>) {
    if (!session.authToken) throw new Error("Cannot create PermissionsAPIService without session.");
    this.headers.set(
      "Authorization",
      `Bearer ${session.authToken.value}`
    );
  }

  async addPermission(
    permission: Permission,
    abortController?: AbortController
  ): Promise<Permission> {
    const response = await axios.post(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions`,
      new PermissionCommand(permission),
      {
        headers: this.headersWithJson(),
        signal: abortController?.signal
      }
    );

    const apiResponse = Object.assign(
      new PermissionAPIResponse(),
      response.data
    );
    return await apiResponse.deserialize();
  }

  async bulkAddPermissions(
    permissions: Permission[],
    abortController?: AbortController
  ): Promise<Permission[]> {
    const response = await axios.post(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions/bulk`,
      new BulkPermissionCommand(permissions),
      {
        headers: this.headersWithJson(),
        signal: abortController?.signal
      }
    );

    const responseData: Permission[] = [];
    for (const datum of response.data) {
      const apiResponse: PermissionAPIResponse = Object.assign(
        new PermissionAPIResponse(),
        datum
      );
      const responseDatum = await apiResponse.deserialize();
      responseData.push(responseDatum);
    }
    return responseData;
  }

  async getPermissions(
    query: PermissionQuery,
    abortController?: AbortController
  ): Promise<PaginatedResponse<Permission>> {
    const response = await axios.get(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions?${query.asSearchParams().toString()}`,
      {
        headers: this.headers,
        signal: abortController?.signal
      }
    );

    const responseData: Permission[] = [];
    for (const datum of response.data) {
      const apiResponse: PermissionAPIResponse = Object.assign(
        new PermissionAPIResponse(),
        datum
      );
      const responseDatum = await apiResponse.deserialize();
      responseData.push(responseDatum);
    }

    return PaginatedResponse.from<Permission>(
      responseData, response.headers['x-pagination']
    )
  }

  async getPermissionsByGrantee(
    query: PermissionQuery,
    abortController?: AbortController
  ): Promise<PaginatedResponse<PermissionsByGranteeId>> {
    const response = await axios.get(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions?${query.asSearchParams().toString()}`,
      {
        headers: this.headers,
        signal: abortController?.signal
      }
    );

    const responseData: PermissionsByGranteeId[] = [];
    for (const datum of response.data) {
      const granteeUserId = Guid.fromJSON(datum.granteeUserId);
      if (!granteeUserId) continue;
      const permissions: Permission[] = [];
      for (const perm of datum.recentPermissionsPreview) {
        const apiResponse = Object.assign(new PermissionAPIResponse(), perm);
        const permission = await apiResponse.deserialize();
        permissions.push(permission);
      }
      const response: PermissionsByGranteeId = {
        id: granteeUserId,
        granteeUserId: granteeUserId,
        totalPermissions: datum.totalPermissions,
        permissionCounts: datum.permissionCounts,
        recentPermissionsPreview: permissions
      };
      responseData.push(response);
    }

    return PaginatedResponse.from<PermissionsByGranteeId>(responseData, response.headers['x-pagination']);
  }

  async disablePermission(
    permission: Permission,
    abortController?: AbortController
  ): Promise<Permission> {
    if (!permission.id) {
      throw new Error("Permission ID is required to disable a permission.");
    }
    const response = await axios.delete(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions/${permission.id.value}`,
      {
        headers: this.headers,
        signal: abortController?.signal
      }
    );
    const apiResponse = Object.assign(
      new PermissionAPIResponse(),
      response.data
    );
    
    return await apiResponse.deserialize();
  }

  async enablePermission(
    permission: Permission,
    abortController?: AbortController
  ): Promise<Permission> {
    if (!permission.id) {
      throw new Error("Permission ID is required to enable a permission.");
    }
    const response = await axios.post(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions/${permission.id.value}/enable`,
      {},
      {
        headers: this.headers,
        signal: abortController?.signal
      }
    );
    const apiResponse = Object.assign(
      new PermissionAPIResponse(),
      response.data
    );
    
    return await apiResponse.deserialize();
  }
}

export class PermissionsAPIServiceError extends Error {
  public methodName: string;
  public error: unknown;

  constructor(methodName: string, error: unknown) {
    super(`Error in PermissionsAPIService.${methodName}: ${error}`);
    this.methodName = methodName;
    this.error = error;
  }
}
export type PermissionsByGranteeId = {
  id: Guid;
  granteeUserId: Guid;
  totalPermissions: number;
  permissionCounts: Record<string, number>;
  recentPermissionsPreview: Permission[];
};
